<template>
  <Dialog
    v-model:visible="isVisible"
    dismissable-mask
    :draggable="false"
    header="Duplicate Reward"
    :modal="true"
    :style="{ width: '30vw' }"
    @hide="onCancel"
  >
    <div>
      <label for="duplicateRewardName" style="font-size: 12px">Enter a new name</label>
      <InputText
        id="duplicateRewardName"
        v-model="duplicateRewardName"
        :class="[$style.inputText, error ? 'p-invalid' : '']"
      />
      <small v-if="error" :class="$style.errorText">
        {{ error }}
      </small>
    </div>
    <template #footer>
      <Button label="Cancel" severity="primary" outlined @click="() => onCancel(false)" />
      <Button label="Duplicate" severity="primary" @click="onConfirm" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

import isAlphaNumeric from "@/utils/isAlphaNumeric";
import useRewards from "../hooks/useRewards";

const props = defineProps({
  isVisible: Boolean,
  selectedReward: Object,
});

const emit = defineEmits(["update:isVisible", "cancel"]);

const route = useRoute();
const gameId = ref(route.params.gameId as string);
const versionId = ref(route.params.versionId as string);

const duplicateRewardName = ref("");
const error = ref("");

const { duplicateReward, rewardsToDisplay, fetchRewards } = useRewards(gameId.value, versionId.value);

const isVisible = computed({
  get: () => props.isVisible,
  set: () => {
    emit("cancel");
  },
});

const onCancel = (refetch = false) => {
  duplicateRewardName.value = "";
  error.value = "";

  emit("cancel", refetch);
};

const onConfirm = async () => {
  const trimmedName = duplicateRewardName.value.trim();

  if (!trimmedName) {
    error.value = "Name cannot be empty.";
    return;
  }

  if (rewardsToDisplay.value.some((reward) => reward?.name === trimmedName)) {
    error.value = "Reward with this name already exists.";
    return;
  }

  if (!isAlphaNumeric({ str: trimmedName, allowWhitespace: true })) {
    error.value = "Name must be alphanumeric and can include spaces, but start with a letter";
    return;
  }

  await duplicateReward(props.selectedReward?.id, trimmedName);
  onCancel(true);
};

onMounted(() => {
  fetchRewards();
});
</script>

<style module lang="scss">
.errorText {
  display: block;
  color: #e24c4c;
}

.inputText {
  margin: 0.25rem 0;
  width: 100%;
}
</style>
